import React, { useState, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import styled from "styled-components";
import Title from "../../components/Title";
import Body from "../../layout/Body";
import { getAchievements } from "../../../../api/AchievementController";
import { CACHE_QUERY_OPTIONS } from "../../../../constants";
import { formatDateTimeWithTimeZone } from "../../../../utils/generalUtils";
import Typography from "../../../../components/common/text/Typography";
import { setColor } from "../../../../assets/styles/styles";
import DataTable from "../../../../components/common/dataTable/DataTable";
import Pagination from "../../../../components/common/pagination/Pagination";
import AchievementsFields from "./AchievementsFields";
import Modal from "../../components/Modal";
import AddAchievement from "./AddAchievement";
import { default as Button } from "../../../../components/common/button/Button";
import { ReactComponent as CheckmarkIcon } from "../../../../assets/images/icons/Checkmark.svg";
import { ReactComponent as Cross } from "../../../../assets/images/icons/Cross.svg";

const AchievementsPage = () => {
    const ROWS_PER_PAGE = 5;
    const [visibleRows, setVisibleRows] = useState([]);
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(0);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [selectedRow, setSelectedRow] = useState(null);
    const [isAddModalOpen, setIsAddModalOpen] = useState(false);

    const {
        data: achievementsData,
        error,
        isLoading,
        refetch,
    } = useQuery(["achievementsData"], () => getAchievements(), {
        ...CACHE_QUERY_OPTIONS(),
    });

    useEffect(() => {
        if (achievementsData) {
            const sorted = [...achievementsData].sort(
                (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
            );

            setTotalPages(Math.ceil(sorted.length / ROWS_PER_PAGE));
            setVisibleRows(sorted.slice((currentPage - 1) * ROWS_PER_PAGE, currentPage * ROWS_PER_PAGE));
        }
    }, [achievementsData, currentPage]);

    const renderRow = (row) => {
        let activeOrNot;
        if (row.active === true) {
            activeOrNot = <StyledCheckmark />;
        } else {
            activeOrNot = <StyledCloseIcon />;
        }

        return [
            row.id,
            row.title,
            row.description,
            row.type,
            activeOrNot,
            formatDateTimeWithTimeZone(row.createdAt),
            // <>{DetachAchvmt}</>,
        ];
    };

    const openModal = (row) => {
        setSelectedRow(row);
        setIsModalOpen(true);
    };

    return (
        <>
            <CardTable>
                <TableRow>
                    <Title title={"Achievements Overview"} description={"Manage your achievements here."} />

                    <TableColumn>
                        <Button
                            variant={"OutlinedButton"}
                            label={"Add Achievement"}
                            margin={"2rem 0 0 0"}
                            onClick={() => setIsAddModalOpen(true)}
                        />
                    </TableColumn>
                </TableRow>
            </CardTable>

            <Body isLoading={isLoading}>
                <>
                    {achievementsData && (
                        <>
                            <DataTable
                                headers={["ACHIEVEMENT ID", "TITLE", "DESCRIPTION", "TYPE", "ACTIVE", "CREATED AT"]}
                                data={visibleRows}
                                renderRow={renderRow}
                                openModal={openModal}
                                isModalOpen={isModalOpen}
                                selectedRow={selectedRow}
                                detailsComponent={AchievementsFields}
                            />
                            <Pagination
                                totalNumber={achievementsData.length}
                                visibleRows={visibleRows}
                                currentPage={currentPage}
                                setCurrentPage={setCurrentPage}
                                totalPages={totalPages}
                                tag={"Achievements"}
                            />
                            <Modal
                                isOpen={isModalOpen}
                                setIsOpen={setIsModalOpen}
                                children={<AchievementsFields row={selectedRow} />}
                            />

                            {isAddModalOpen && (
                                <Modal
                                    isOpen={isAddModalOpen}
                                    setIsOpen={setIsAddModalOpen}
                                    children={
                                        <AddAchievement
                                            closeModal={() => setIsAddModalOpen(false)}
                                            refetchAchievements={refetch}
                                        />
                                    }
                                />
                            )}
                        </>
                    )}
                </>
            </Body>
        </>
    );
};

export default AchievementsPage;

const CardTable = styled.div`
    display: flex;
    flex-direction: column;
`;

const TableRow = styled.div`
    display: flex;

    @media (max-width: 768px) {
        flex-direction: column;
    }
`;

const TableColumn = styled.div`
    flex: 1;
    @media (max-width: 768px) {
        font-size: 14px;
    }
`;

const FilterContainer = styled.div`
    display: flex;
    align-items: flex-start;
    width: 100%;
`;

const MessageCenter = styled.div`
    color: ${(props) => (props.signal ? setColor.errorColor : setColor.successColor)};
    margin: 1rem 0rem;
    display: flex;
    justify-content: flex-end;
`;

const StyledCheckmark = styled(CheckmarkIcon)`
    width: 12px;
    height: 12px;
    margin: 1.5rem;
    #Union {
        fill: ${setColor.successColor};
    }
`;

const StyledCloseIcon = styled(Cross)`
    width: 12px;
    height: 12px;
    margin: 1.5rem;
    #Union {
        fill: ${setColor.spanTextColor};
    }
`;
